/**
 * Lineup Validation Utilities
 * 
 * Comprehensive validation rules for match lineups used across
 * the strategize tab and manual results entry.
 */

export interface ValidationError {
  matchId: number;
  positionNumber: number;
  field?: 'player1_id' | 'player2_id' | 'match_type';
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export interface Player {
  id: number;
  name: string;
  gender?: 'male' | 'female' | null;
  ntrp_rating?: number | null;
}

export interface LineupPosition {
  position_number: number;
  player1_id: number;
  player2_id?: number | null;
}

export interface LineupFormatPosition {
  position_number: number;
  match_type: string;
}

export interface LineupFormat {
  total_lines: number;
  max_sets_per_line: number;
  allow_multi_line_selection: boolean;
  positions: LineupFormatPosition[];
}

/**
 * Check if a match type is singles
 */
export function isSinglesPosition(matchType: string): boolean {
  if (!matchType) return false;
  const lowerType = matchType.toLowerCase();
  return lowerType.includes('singles') && !lowerType.includes('doubles');
}

/**
 * Check if a match type is doubles
 */
export function isDoublesPosition(matchType: string): boolean {
  if (!matchType) return false;
  const lowerType = matchType.toLowerCase();
  return lowerType.includes('doubles');
}

/**
 * Check if a match type requires specific gender
 */
function isMensMatchType(matchType: string): boolean {
  if (!matchType) return false;
  const lowerType = matchType.toLowerCase();
  return lowerType.startsWith('mens_');
}

function isWomensMatchType(matchType: string): boolean {
  if (!matchType) return false;
  const lowerType = matchType.toLowerCase();
  return lowerType.startsWith('womens_');
}

function isMixedDoublesMatchType(matchType: string): boolean {
  if (!matchType) return false;
  const lowerType = matchType.toLowerCase();
  return lowerType === 'mixed_doubles';
}

/**
 * Validate gender compatibility for a match type
 */
function isGenderCompatible(
  playerGender: 'male' | 'female' | null | undefined,
  matchType: string,
  otherPlayerGender?: 'male' | 'female' | null | undefined
): { compatible: boolean; reason?: string } {
  // Unset gender is always allowed
  if (!playerGender) {
    return { compatible: true };
  }

  const playerGenderLower = playerGender.toLowerCase();
  const matchTypeLower = matchType.toLowerCase();

  // Men's matches
  if (isMensMatchType(matchTypeLower)) {
    if (playerGenderLower !== 'male') {
      return {
        compatible: false,
        reason: `${matchType} requires male players or unset gender`
      };
    }
    return { compatible: true };
  }

  // Women's matches
  if (isWomensMatchType(matchTypeLower)) {
    if (playerGenderLower !== 'female') {
      return {
        compatible: false,
        reason: `${matchType} requires female players or unset gender`
      };
    }
    return { compatible: true };
  }

  // Mixed doubles
  if (isMixedDoublesMatchType(matchTypeLower)) {
    if (otherPlayerGender) {
      const otherGenderLower = otherPlayerGender.toLowerCase();
      // If both have gender set, they must be different
      if (playerGenderLower === otherGenderLower) {
        return {
          compatible: false,
          reason: `Mixed doubles requires one male and one female player`
        };
      }
    }
    // If other player doesn't have gender set, allow any gender
    return { compatible: true };
  }

  // Unknown match type - allow
  return { compatible: true };
}

/**
 * Validate a single lineup position
 */
function validatePosition(
  lineupPos: LineupPosition,
  formatPos: LineupFormatPosition | undefined,
  players: Player[],
  teamMemberIds: Set<number>,
  matchId: number,
  maxCombinedNtrpRating?: number | null
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!formatPos) {
    errors.push({
      matchId,
      positionNumber: lineupPos.position_number,
      field: 'match_type',
      message: `Position ${lineupPos.position_number}: Match type not configured for this position`
    });
    return errors;
  }

  const matchType = formatPos.match_type;
  const isSingles = isSinglesPosition(matchType);
  const isDoubles = isDoublesPosition(matchType);

  // Validate player1 is required
  if (!lineupPos.player1_id || lineupPos.player1_id === 0) {
    // Empty positions are allowed (incomplete lineups)
    return errors;
  }

  // Validate player1 is a team member
  if (!teamMemberIds.has(lineupPos.player1_id)) {
    errors.push({
      matchId,
      positionNumber: lineupPos.position_number,
      field: 'player1_id',
      message: `Player 1 is not a team member`
    });
  }

  const player1 = players.find(p => p.id === lineupPos.player1_id);

  // Validate doubles positions (partial lineups are allowed - either player can be empty)
  if (isDoubles) {
    // Validate player1's gender compatibility with match type (even if player2 is not set)
    if (player1) {
      const genderCheck = isGenderCompatible(
        player1.gender,
        matchType,
        undefined // No partner yet
      );
      if (!genderCheck.compatible) {
        errors.push({
          matchId,
          positionNumber: lineupPos.position_number,
          field: 'player1_id',
          message: `Position ${lineupPos.position_number}: ${player1.name} - ${genderCheck.reason || 'Gender incompatible'}`
        });
      }
    }

    // Only validate player2-related rules when player2_id is present
    if (lineupPos.player2_id) {
      // Validate player2 is a team member
      if (!teamMemberIds.has(lineupPos.player2_id)) {
        errors.push({
          matchId,
          positionNumber: lineupPos.position_number,
          field: 'player2_id',
          message: `Player 2 is not a team member`
        });
      } else {
        // Validate player1 and player2 are different
        if (lineupPos.player1_id === lineupPos.player2_id) {
          errors.push({
            matchId,
            positionNumber: lineupPos.position_number,
            field: 'player2_id',
            message: `The same player cannot be assigned to both Player 1 and Player 2`
          });
        }

        const player2 = players.find(p => p.id === lineupPos.player2_id);
        
        // Validate NTRP rating sum for doubles if maxCombinedNtrpRating is set
        // Only validate when both players are present
        if (maxCombinedNtrpRating !== null && maxCombinedNtrpRating !== undefined && player1 && player2) {
          const player1Rating = player1.ntrp_rating;
          const player2Rating = player2.ntrp_rating;
          
          if (player1Rating !== null && player1Rating !== undefined && 
              player2Rating !== null && player2Rating !== undefined) {
            const combinedRating = player1Rating + player2Rating;
            if (combinedRating > maxCombinedNtrpRating) {
              errors.push({
                matchId,
                positionNumber: lineupPos.position_number,
                field: 'player2_id',
                message: `Combined NTRP rating (${combinedRating.toFixed(1)}) exceeds maximum (${maxCombinedNtrpRating.toFixed(1)})`
              });
            }
          }
        }
        
        // Validate gender requirements for both players when both are present
        if (player1 && player2) {
          const genderCheck1 = isGenderCompatible(
            player1.gender,
            matchType,
            player2.gender
          );
          if (!genderCheck1.compatible) {
            errors.push({
              matchId,
              positionNumber: lineupPos.position_number,
              field: 'player1_id',
              message: `Position ${lineupPos.position_number}: ${player1.name} - ${genderCheck1.reason || 'Gender incompatible'}`
            });
          }

          const genderCheck2 = isGenderCompatible(
            player2.gender,
            matchType,
            player1.gender
          );
          if (!genderCheck2.compatible) {
            errors.push({
              matchId,
              positionNumber: lineupPos.position_number,
              field: 'player2_id',
              message: `Position ${lineupPos.position_number}: ${player2.name} - ${genderCheck2.reason || 'Gender incompatible'}`
            });
          }
        }
      }
    }
  } else if (isSingles) {
    // Singles should not have player2
    if (lineupPos.player2_id) {
      errors.push({
        matchId,
        positionNumber: lineupPos.position_number,
        field: 'player2_id',
        message: `Position ${lineupPos.position_number} (${matchType}) should only have one player`
      });
    }

    // Validate gender requirements for singles
    if (player1) {
      const genderCheck = isGenderCompatible(player1.gender, matchType);
      if (!genderCheck.compatible) {
        errors.push({
          matchId,
          positionNumber: lineupPos.position_number,
          field: 'player1_id',
          message: `Position ${lineupPos.position_number}: ${player1.name} - ${genderCheck.reason || 'Gender incompatible'}`
        });
      }
    }
  }

  return errors;
}

/**
 * Validate multi-line selection rules
 */
function validateMultiLineSelection(
  matchLineup: LineupPosition[],
  lineupFormat: LineupFormat,
  players: Player[],
  matchId: number
): ValidationError[] {
  const errors: ValidationError[] = [];

  // If multi-line selection is allowed, skip this validation
  if (lineupFormat.allow_multi_line_selection) {
    return errors;
  }

  // Track which players are assigned to which positions
  const playerPositions: { [playerId: number]: number[] } = {};

  matchLineup.forEach(pos => {
    if (pos.player1_id && pos.player1_id > 0) {
      if (!playerPositions[pos.player1_id]) {
        playerPositions[pos.player1_id] = [];
      }
      playerPositions[pos.player1_id].push(pos.position_number);
    }
    if (pos.player2_id && pos.player2_id > 0) {
      if (!playerPositions[pos.player2_id]) {
        playerPositions[pos.player2_id] = [];
      }
      playerPositions[pos.player2_id].push(pos.position_number);
    }
  });

  // Check for players assigned to multiple positions
  Object.entries(playerPositions).forEach(([playerId, positions]) => {
    if (positions.length > 1) {
      const player = players.find(p => p.id === parseInt(playerId));
      errors.push({
        matchId,
        positionNumber: positions[0],
        message: `${player?.name || 'Player'} is assigned to multiple positions (${positions.join(', ')}) in the same match. Multi-line selection is not allowed.`
      });
    }
  });

  return errors;
}

/**
 * Comprehensive lineup validation for a single match
 */
export function validateMatchLineup(
  matchId: number,
  matchLineup: LineupPosition[],
  lineupFormat: LineupFormat,
  players: Player[],
  teamMemberIds: number[],
  maxCombinedNtrpRating?: number | null
): ValidationResult {
  const errors: ValidationError[] = [];
  const teamMemberIdsSet = new Set(teamMemberIds);

  // Validate each position
  matchLineup.forEach(lineupPos => {
    const formatPos = lineupFormat.positions.find(
      p => p.position_number === lineupPos.position_number
    );
    
    const positionErrors = validatePosition(
      lineupPos,
      formatPos,
      players,
      teamMemberIdsSet,
      matchId,
      maxCombinedNtrpRating
    );
    
    errors.push(...positionErrors);
  });

  // Validate multi-line selection rules
  const multiLineErrors = validateMultiLineSelection(
    matchLineup,
    lineupFormat,
    players,
    matchId
  );
  
  errors.push(...multiLineErrors);

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validate a specific field change (for real-time validation)
 */
export function validateFieldChange(
  matchId: number,
  positionNumber: number,
  field: 'player1_id' | 'player2_id',
  value: number | null,
  currentLineup: LineupPosition[],
  lineupFormat: LineupFormat,
  players: Player[],
  teamMemberIds: number[],
  maxCombinedNtrpRating?: number | null
): ValidationError[] {
  // Create a temporary lineup with the new value
  const tempLineup = currentLineup.map(pos => {
    if (pos.position_number === positionNumber) {
      return {
        ...pos,
        [field]: value || null
      };
    }
    return pos;
  });

  // If position doesn't exist, add it
  if (!tempLineup.find(p => p.position_number === positionNumber)) {
    tempLineup.push({
      position_number: positionNumber,
      player1_id: field === 'player1_id' ? (value || 0) : 0,
      player2_id: field === 'player2_id' ? (value || null) : null
    });
  }

  // Validate the entire match lineup
  const result = validateMatchLineup(
    matchId,
    tempLineup,
    lineupFormat,
    players,
    teamMemberIds,
    maxCombinedNtrpRating
  );

  // Filter errors to only this position and field
  return result.errors.filter(
    err => err.positionNumber === positionNumber && (err.field === field || !err.field)
  );
}
